import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { advisorApi } from '../../api/endpoints.js';
import { stream } from '../../api/client.js';
import { useNotice } from '../../state/NoticeContext.jsx';
import {
  Banner,
  Button,
  Card,
  Grid2,
  Muted,
  Spinner,
  Tag,
  fmtDate,
  useAsync
} from '../../ui.jsx';

const statusTone = { yes: 'ok', fp: 'mid', reach: 'mid', long: 'no', no: 'no' };

/** If the model still wraps its answer as {"answer":"..."}, show only the text. */
const unwrapAnswer = (text) => {
  const t = String(text ?? '').trim();
  if (t.startsWith('{"answer":') && t.endsWith('}')) {
    try {
      const parsed = JSON.parse(t);
      if (typeof parsed.answer === 'string') return parsed.answer;
    } catch {
      // Not JSON after all; show it as-is.
    }
  }
  return text;
};

export default function Advisor() {
  const navigate = useNavigate();
  const notice = useNotice();
  const { data, loading, error } = useAsync(() => advisorApi.brief(), []);

  const [advice, setAdvice] = useState(null);
  const [loadingAdvice, setLoadingAdvice] = useState(false);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [history, setHistory] = useState([]);
  const box = useRef(null);
  const answerRef = useRef('');

  if (loading) return <Spinner label="Loading your advisor" />;
  if (error) return <div className="note">We could not load the advisor. Please try again.</div>;

  if (!data.ready) {
    return (
      <Card>
        <h2>AI advisor</h2>
        <Muted>{data.message}</Muted>
        <div style={{ marginTop: 12 }}>
          <Button onClick={() => navigate('/match')}>Add your results</Button>
        </div>
      </Card>
    );
  }

  const getAdvice = async () => {
    setLoadingAdvice(true);
    try {
      const result = await advisorApi.advice({});
      setAdvice(result);
      notice.success(result.message);
    } catch (err) {
      notice.fromError(err);
    } finally {
      setLoadingAdvice(false);
    }
  };

  const ask = async (event) => {
    event.preventDefault();
    const text = question.trim();
    if (!text || streaming) return;

    const nextHistory = [...history, { role: 'user', content: text }];
    setHistory(nextHistory);
    setQuestion('');
    answerRef.current = '';
    setAnswer('');
    setStreaming(true);

    try {
      await stream('/api/advisor/chat', { question: text, history: nextHistory }, (event) => {
        if (event.event === 'message' && event.text) {
          answerRef.current += event.text;
          setAnswer(answerRef.current);
        }
        if (event.event === 'done') {
          const final = unwrapAnswer(answerRef.current);
          setAnswer(final);
          setHistory((h) => [...h, { role: 'assistant', content: final }]);
          setStreaming(false);
        }
      });
    } catch (err) {
      notice.fromError(err);
      setStreaming(false);
    } finally {
      if (box.current) box.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  return (
    <>
      <h2>AI advisor</h2>
      <Muted>
        Entrance checks your grades against each school's rules first. The advisor explains those
        results and suggests what to improve. It only advises: each college decides.
      </Muted>

      <Grid2>
        <Card style={{ textAlign: 'center' }}>
          <Muted>Your aggregate</Muted>
          <div className="big" style={{ font: '800 54px/1 "Bricolage Grotesque", sans-serif', color: 'var(--g)' }}>
            {data.aggregate}
          </div>
          <Tag tone={data.aggregate <= 18 ? 'ok' : 'mid'}>{data.band}</Tag>
          <div className="mu" style={{ marginTop: 10 }}>{data.grades}</div>
        </Card>

        <Card>
          <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>Where you stand</h3>
          <div className="stats" style={{ margin: 0 }}>
            {[
              ['Qualified', data.stats.qualified],
              ['Fee-paying', data.stats.feePaying],
              ['Reach', data.stats.reach],
              ['Long shot', data.stats.longShot]
            ].map(([label, value]) => (
              <div className="stat" key={label}>
                <b>{value}</b>
                <span className="mu">{label}</span>
              </div>
            ))}
          </div>
          {data.interests?.length ? (
            <p className="mu" style={{ marginTop: 10 }}>Interests: {data.interests.join(', ')}.</p>
          ) : null}
          {data.careerGoal ? <p className="mu">Career goal: {data.careerGoal}.</p> : null}
        </Card>
      </Grid2>

      <Card>
        <div className="row sp">
          <b>Advice</b>
          <Button size="sm" busy={loadingAdvice} onClick={getAdvice}>
            {advice ? 'Refresh advice' : 'Get advice'}
          </Button>
        </div>

        <div className="mu" style={{ marginTop: 6 }}>
          {data.aiAvailable
            ? 'The model explains the rule engine and adds suggestions.'
            : 'The model is not configured, so this view uses rule-based advice only.'}
        </div>

        {advice ? (
          <div style={{ marginTop: 14 }}>
            <p>{advice.advice.summary}</p>

            {advice.advice.picks?.length ? (
              advice.advice.picks.map((pick) => (
                <div className="prog" key={pick.id} style={{ alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <b>{pick.programmeName}</b>
                    <div className="mu">{pick.school} · {pick.fieldLabel}</div>
                    <div className="mu">{pick.why}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <Tag tone={statusTone[pick.status]}>{pick.statusLabel}</Tag>
                    <div className="mu" style={{ marginTop: 4 }}>
                      Cut-off {pick.cutOff ?? 'not set'}
                      {pick.cutOffEstimated ? ' (est.)' : ''}
                    </div>
                  </div>
                </div>
              ))
            ) : null}

            {advice.advice.improve?.length ? (
              <>
                <p className="hd">What would widen your options</p>
                <ul className="req">
                  {advice.advice.improve.map((line) => <li key={line}>{line}</li>)}
                </ul>
              </>
            ) : null}

            {advice.advice.warnings?.length ? (
              <Banner title="Keep in mind">
                <ul className="req" style={{ margin: 0 }}>
                  {advice.advice.warnings.map((line) => <li key={line}>{line}</li>)}
                </ul>
              </Banner>
            ) : null}

            {advice.advice.plan ? (
              <>
                <p className="hd">Your plan</p>
                <p className="mu">{advice.advice.plan}</p>
              </>
            ) : null}
          </div>
        ) : (
          <Muted>
            Nothing has been generated yet. Advice is built from your grades, so add them first if the
            list is empty.
          </Muted>
        )}
      </Card>

      {data.blocked?.length ? (
        <Card>
          <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>Programmes below your grades</h3>
          <Muted>
            These are listed so you know the gap, not to discourage you. Requirements can differ from
            one year to the next.
          </Muted>
          {data.blocked.slice(0, 8).map((row) => (
            <div className="prog" key={row.programmeId || row.id || row.name}>
              <div style={{ flex: 1 }}>
                <b>{row.programmeName || row.name}</b>
                <div className="mu">{row.schoolName || row.school} · {row.reason}</div>
              </div>
            </div>
          ))}
        </Card>
      ) : null}

      <Card>
        <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>Ask a question</h3>
        <Muted>
          Answers are based on your profile and Entrance's data, and are kept short.
        </Muted>

        {history.length ? (
          <div style={{ marginTop: 12 }}>
            {history.map((message, i) => (
              <div className="prog" key={`${message.role}-${i}`} style={{ alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <b>{message.role === 'user' ? 'You' : 'Advisor'}</b>
                  <div style={{ whiteSpace: 'pre-wrap' }}>
                    {message.content || (i === history.length - 1 ? answer : '')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {streaming && answer ? (
          <div className="prog" style={{ alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }} ref={box}>
              <b>Advisor</b>
              <div style={{ whiteSpace: 'pre-wrap' }}>{answer}</div>
            </div>
          </div>
        ) : null}

        <form onSubmit={ask} style={{ marginTop: 14 }}>
          <label>
            <textarea
              rows={3}
              value={question}
              disabled={streaming}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="For example: which of my programmes should I apply to first?"
            />
          </label>
          <div style={{ marginTop: 10 }}>
            <Button type="submit" busy={streaming} disabled={!question.trim()}>Ask</Button>
          </div>
        </form>
      </Card>
    </>
  );
}
